import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { Avatar, Cell, Label, Row, Tag, Text } from 'components';
import { theme, toRem } from 'design';
import { DisbursementMethod, IEmployee } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';

interface IPayoutRowProps {
  employee?: IEmployee;
  job: string;
  tipAmount: number;
  holdbackAmount: number;
  disbursementMethod: DisbursementMethod;
  siteUsesHoldbacks: boolean;
  isLoading: boolean;
  hasError?: boolean;
  dataCy?: string;
}

interface IPayoutRowStyleProps {
  hasError: boolean;
}

const StyledRow = styled(Row)<IPayoutRowStyleProps>`
  ${({ hasError }) =>
    hasError &&
    css`
      background-color: ${theme.color.warning.light};
      &:hover {
        background-color: ${theme.color.warning.light};
      }
    `}
`;

const FlexCell = styled(Cell)`
  display: flex;
  align-items: center;
  background: inherit !important;

  .employee-info {
    display: flex;
    flex-direction: column;
    padding-left: ${toRem(16)};
  }
`;

export const PayoutRow = ({ employee, job, tipAmount, holdbackAmount, disbursementMethod, siteUsesHoldbacks, isLoading, hasError, dataCy }: IPayoutRowProps) => {
  const totalPaid = tipAmount - holdbackAmount; // subtract holdback from tip amount to calculate total paid.

  return (
    <StyledRow hasError={hasError} data-error={hasError} data-cy={`${dataCy}`}>
      <FlexCell>
        <Avatar data-cy={`${dataCy}-avatar`} size="small" username={employee?.name} id={employee?.id} image={employee?.user?.avatarUrl} hasPaymentMethod={!!employee?.user?.card} isLoading={isLoading} />
        <div className="employee-info">
          {isLoading ? <Skeleton width={150} /> : <Label data-cy={`${dataCy}-name`}>{employee?.name}</Label>}
          {isLoading ? (
            <Skeleton width={150} />
          ) : (
            <Text size="small" data-cy={`${dataCy}-job`}>
              {job}
            </Text>
          )}
        </div>
      </FlexCell>
      {siteUsesHoldbacks && <Cell data-cy={`${dataCy}-tip-amount`}>{isLoading ? <Skeleton width={100} /> : centsToDollarsDisplay(tipAmount)}</Cell>}
      {siteUsesHoldbacks && <Cell data-cy={`${dataCy}-holdback-amount`}>{isLoading ? <Skeleton width={100} /> : centsToDollarsDisplay(holdbackAmount)}</Cell>}
      <Cell data-cy={`${dataCy}-paid`}>{isLoading ? <Skeleton width={100} /> : centsToDollarsDisplay(totalPaid)}</Cell>
      <Cell data-cy={`${dataCy}-disbursement-method`} align="right">
        {isLoading ? <Skeleton width={100} /> : <Tag appearance={disbursementMethod} label={disbursementMethod} />}
      </Cell>
    </StyledRow>
  );
};
